import React from 'react';
import { capitalize, isEmpty, isNil } from 'lodash';

import { Text } from '~/components/core/TextComponents';
import { Sparkle } from '~/components/icons';
import OverflowTextWithToolTip from '~/components/OverflowTextWithToolTip';

/** A confidence as it reads: the level where there is one, else the raw score; `null` for neither. */
export const formatConfidence = ({ level, score }: { level?: string; score?: number }): string | null =>
  !isEmpty(level) ? capitalize(level) : isNil(score) ? null : String(score);

/**
 * The Insights confidence line as a chip, for a card's header: the same reading, without the
 * explanation, at the size of the state chip beside it. Nothing at all where there is no score, so
 * a card without one shows no empty label.
 */
export const ConfidenceChip: React.FC<{ score?: number }> = ({ score }) => {
  const confidenceValue = formatConfidence({ score });
  if (isNil(confidenceValue)) {
    return null;
  }

  return (
    // Never wider than its line: a narrow card cuts the label short rather than the chip spilling out.
    <div className="flex w-fit min-w-0 max-w-full items-center gap-4 rounded-full bg-pink-100 px-12 py-4">
      <Sparkle size={16} animated={false} className="shrink-0" />
      <OverflowTextWithToolTip
        tooltipTitle={`Confidence ${confidenceValue}`}
        textWrapperClassName="data-[overflowing=true]:relative data-[overflowing=true]:z-10"
      >
        <Text variant={Text.VARIANTS.XS} colorVariant={Text.COLOR_VARIANTS.BLACK} className="inline">
          Confidence{' '}
        </Text>
        <Text
          variant={Text.VARIANTS.XS}
          weight={Text.WEIGHTS.SEMI_BOLD}
          colorVariant={Text.COLOR_VARIANTS.PRIMARY}
          className="inline"
        >
          {confidenceValue}
        </Text>
      </OverflowTextWithToolTip>
    </div>
  );
};
